import { PromotionProvidersResponse } from '@chargeafter/payment-types';

type CalculateMonthlyPaymentProps = {
  amount: string | number;
} & Pick<
  PromotionProvidersResponse['providers'][0]['promotions'][0]['terms'],
  'apr' | 'numberOfPeriods' | 'deferredNumberOfPeriods'
>;

/**
 * Standard Calculating monthly payment for a loan,
 * has logic for both APR = 0 and APR > 0.
 */
const standardCalculate = ({
  amount: requestedAmount, // the amount of the loan requested by the customer
  monthlyRate, // monthly interest rate (APR / 100 / 12)
  numberOfPeriods, // this is the number of months the customer will pay (e.g. 360 for 30 years)
}: Pick<CalculateMonthlyPaymentProps, 'numberOfPeriods'> & {
  amount: number;
  monthlyRate: number;
}) => {
  const fixedValue = (value: number) => {
    return Number(value.toFixed(2));
  };

  if (monthlyRate > 0) {
    const numerator = monthlyRate * Math.pow(1 + monthlyRate, numberOfPeriods);
    const denominator = Math.pow(1 + monthlyRate, numberOfPeriods) - 1;

    return fixedValue(requestedAmount * (numerator / denominator));
  } else if (numberOfPeriods > 0) {
    return fixedValue(
      Math.ceil((requestedAmount / numberOfPeriods) * 100) / 100
    );
  }

  throw new Error(
    `Payment period cannot be zero or negative, received: ${numberOfPeriods}`
  );
};

/**
 * Calculates the monthly payment for a loan considering deferment months.
 */
export const calculateMonthlyPayment = ({
  amount, // the amount of the loan requested by the customer
  apr: APR, // annual percentage rate (APR)
  numberOfPeriods: period, // this is the number of months the customer will pay (e.g. 360 for 30 years)
  deferredNumberOfPeriods: monthsFree, // this is the number of months the customer is not gonna pay (e.g similar to totalAmount - (monthlyPayment*defermentMonths))
}: CalculateMonthlyPaymentProps) => {
  const apr = Number(APR || 0);
  const numberOfPeriods = Number(period);
  const requestedAmount = Number(amount);
  const defermentMonths = Number(monthsFree || 0);

  if (
    isNaN(defermentMonths) ||
    isNaN(requestedAmount) ||
    isNaN(apr) ||
    isNaN(numberOfPeriods)
  ) {
    throw new Error(
      `Received invalid parameters, they should be only numbers or strings that can convert to numbers: ${JSON.stringify(
        {
          amount,
          apr: APR,
          numberOfPeriods: period,
          deferredNumberOfPeriods: monthsFree,
        }
      )}`
    );
  }

  const monthlyRate = apr / 100 / 12;

  const defermentInterestAmount =
    requestedAmount * Math.pow(1 + monthlyRate, defermentMonths);

  return standardCalculate({
    amount: defermentInterestAmount,
    monthlyRate,
    numberOfPeriods,
  });
};
